import { Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import { environment } from "../../environments/environment";
import { jwtDecode } from "jwt-decode";
import { isNativeAppOidcClient } from "./native-app-oidc.util";

export enum Endpoint {
  tfaGetProviders,
}

export interface Response {
  errorCode: number;
  regToken?: string;
  [key: string]: any;
}

export interface GigyaResponse {
  errorCode: number;
  regToken?: string;
  [key: string]: any;
}

export interface Context {
  expiredAt: number;
  clientId: string;
  redirectUri: string;
}

@Injectable({
  providedIn: "root",
})
export class GigyaService {
  public gigyaDown = signal(false);
  public context: Context | null = null;
  private store: any | null = null;
  private readonly GIGYA_API_KEY = environment.apiKey;

  constructor(
    private router: Router,
  ) {
    this.context = this.getContext();
    console.info("GigyaService initialized - context:", this.context);
  }

  public redirectToContinue(): void {
    if (isNativeAppOidcClient()) {
      console.info("redirectToContinue - native app OIDC client, navigating to /continue via Angular Router (no page reload, gigya.oidc.js is already loaded).");
      this.router.navigate(["/continue"], { queryParamsHandling: "preserve" });
      return;
    }
    const url = new URL(window.location.href);
    url.pathname = "/continue";
    url.searchParams.set("oidcEnabled", "true");
    console.info("redirectToContinue - navigating to:", url.toString());
    window.location.href = url.toString();
  }

  public get client(): any {
    return (window as any).gigya;
  }

  public async execute<T extends GigyaResponse>(method: Function, params: Object): Promise<T> {
    const paramKeys = Object.keys(params);
    console.info("GigyaService.execute - calling gigya method with param keys:", paramKeys);
    return new Promise<T>((resolve) => {
      Object.assign(params, {
        callback: (res: T) => {
          console.info("GigyaService.execute - callback received, errorCode:", res.errorCode, "errorMessage:", (res as any).errorMessage);
          if (res.errorCode >= 500000 && res.errorCode < 600000) {
            console.warn("GigyaService.execute - gigya reported a 5xx server error, marking gigyaDown=true. errorCode:", res.errorCode);
            this.gigyaDown.set(true);
          }
          resolve(res);
        }
      });
      method(params);
    });
  }

  public get data(): Object | null {
    return this.store;
  }

  public setData(data: any) {
    this.store = data;
  }

  public validContext(): boolean {
    if (!this.context) {
      console.warn("validContext - no context available (missing or failed to parse)");
      return false;
    }
    const isValid = this.context.expiredAt * 1000 > Date.now();
    if (!isValid) {
      console.warn("validContext - context is present but EXPIRED. expiredAt:", new Date(this.context.expiredAt * 1000).toISOString(), "now:", new Date().toISOString());
    } else {
      console.info("validContext - context present and valid, expires at:", new Date(this.context.expiredAt * 1000).toISOString());
    }
    return isValid;
  }

  /**
   * Right after accounts.login() succeeds, Gigya may still be persisting the
   * session cookie. Confirm it's actually committed before navigating away
   * (e.g. through the picker/continue/OIDC redirect chain), otherwise the
   * user can land on the target app without a usable session.
   */
  public async waitForSession(retries = 1, delayMs = 1500): Promise<boolean> {
    for (let attempt = 0; attempt <= retries; attempt++) {
      console.info(`waitForSession - attempt ${attempt + 1}/${retries + 1}`);
      const session = await this.execute(this.client.accounts.session.verify, {});
      if (session.errorCode === 0) {
        console.info("waitForSession - session confirmed valid on attempt", attempt + 1);
        return true;
      }
      if (attempt < retries) {
        console.info("waitForSession - session not yet valid, retrying after delay", delayMs, "ms");
        await this.delay(delayMs);
      }
    }
    console.warn("waitForSession - exhausted all retries without confirming a valid session");
    return false;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private getContext(): Context | null {
    const ctx = new URLSearchParams(window.location.search).get("context");
    if (!ctx) {
      console.warn("getContext - no 'context' query param present in URL:", window.location.search);
      return null
    }
    try {
      const parsed = jwtDecode(ctx) as any;

      const context = {
        expiredAt: parsed.exp,
        clientId: parsed.client_id,
        redirectUri: parsed.redirect_url,
      };
      console.info("getContext - successfully parsed context JWT:", context);
      return context;
    } catch (err) {
      console.warn("getContext - FAILED to decode context JWT param - treating as no context. This can happen if the param was truncated/mangled (e.g. by a mobile in-app browser or URL length limits). raw param length:", ctx.length, "error:", err);
      return null;
    }
  }
}
